import { AppContext } from '@/App';
import React, { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom';
import { user_columns, User, columnDisplayNames as userColumnDisplayNames } from "@/Components/TeamBuilder/team_table/columns";
import { get_team_columns, TeamMember, columnDisplayNames } from "@/Components/TeamBuilder/team_table/team_columns";
import { ProjectUserRole, TeamMemberColumnVisibility, UserColumnVisibility } from './team_builder';
//import Loading from '@/Components/loading';
import { getRoleIdFromRoleName } from '@/hooks/project-roles';
import { DataTable } from "@/Components/datatable/data-table";
import { ArrowLeftIcon, ArrowRightIcon, ShowFiltersIcon } from '@/Components/ui/Icons/TooltipIcon';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog } from '@radix-ui/react-dialog';
import { useResponsiveZoom } from '@/hooks/use-responsive-zoom';
import { DialogContent, DialogHeader, DialogTrigger } from '@/Components/ui/dialog';
import { TeamBuilderContext } from './team_builder';
import HelpLink from '@/Components/ui/help-link';
import { WizardContext } from '@/Components/wizard/wizardProvider';
import TutorialChecker from '@/Components/Tutorial/TutorialChecker';
import { Tutorial } from '@/lib/utils';
import { getBasicPageTourSteps } from '../wizards/basic_page_tour';

interface TeamBuilderProps {
    typeFilter?: "Team Member" | "Viewer" | "Sponsor" | "Project Manager"
    showHelpLink?: boolean;
}

const TeamBuilder = ({ typeFilter, showHelpLink=false }: TeamBuilderProps) => {
    const app = useContext(AppContext);
    const wizard = useContext(WizardContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();
    const [demoMode, setDemoMode] = useState<boolean>(false);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true); // add a loading state
    const [teamMembers, setTeamMembers] = useState<TeamMember[]>((app.selectedProject ? [...app.sponsors, ...app.projectManagers, ...app.teamMembers] : []).map(user => ({
        ...user,
        projectRole: app.sponsors.find(s => s.id === user.id) ? "Sponsor" :
            app.projectManagers.find(pm => pm.id === user.id) ? "Project Manager" :
                "Team Member"
    })));
    const [demoUsers, setDemoUsers] = useState<User[]>([]);
    const [demoTeamMembers, setDemoTeamMembers] = useState<TeamMember[]>([]);
    const [userSelection, setUserSelection] = React.useState<{ [key: string]: boolean }>({})
    const [teamMemberSelection, setTeamMemberSelection] = React.useState<{ [key: string]: boolean }>({})
    const [showUserFilters, setShowUserFilters] = useState<boolean>(false);
    const [showTeamFilters, setShowTeamFilters] = useState<boolean>(false);
    const [shouldStartTour, setShouldStartTour] = useState(false);
    const tourStartedRef = React.useRef(false);

    useEffect(() => {
        populateDemoData();
    }, []);

    useEffect(() => {
        document.body.style.cursor = 'wait';
        fetch("Users/users")
            .then((response) => response.json())
            .then(users => {
                fetch(`Team/${app.selectedProject?.id}/`)
                    .then((response) => response.json())
                    .then((pur: ProjectUserRole[]) => {
                        // add users that are in the team
                        setTeamMembers(users.filter((user: User) => pur.find((p) => p.userId === user.id)).map((user: User) => {
                            const projectUserRole = pur.find((p) => p.userId === user.id);
                            return { ...user, projectRole: projectUserRole ? projectUserRole.roleName as "Team Member" | "Viewer" | "Sponsor" | "Project Manager" : "Team Member" };
                        }));
                        // remove users that are already in the team
                        setUsers(users.filter((user: User) => !pur.find((p) => p.userId === user.id)));
                    })
                    .catch((error) => console.error("Error fetching data:", error));
            })
            .catch((error) => console.error("Error fetching data:", error))
            .finally(() => {
                document.body.style.cursor = 'default';
                setLoading(false);
            });
    }, [app.selectedProject]);

    React.useEffect(() => {
        if (!demoMode) {
            app.setSponsors(teamMembers.filter((user) => user.projectRole === "Sponsor"));
            app.setProjectManagers(teamMembers.filter((user) => user.projectRole === "Project Manager"));
            app.setTeamMembers(teamMembers.filter((user) => user.projectRole === "Team Member"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamMembers, demoMode]);

    // if (loading) {
    //     return <Loading />;
    // }

    function loadTeamMemberColumnVisibility(): TeamMemberColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Team Members_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                forename: visibilityValues.find((v) => v.columnId === "forename")?.isVisible ?? app.authLevel <= 3,
                surname: visibilityValues.find((v) => v.columnId === "surname")?.isVisible ?? app.authLevel <= 3,
                displayName: visibilityValues.find((v) => v.columnId === "displayName")?.isVisible ?? true,
                email: visibilityValues.find((v) => v.columnId === "email")?.isVisible ?? true,
                projectRole: typeFilter ? false : true,
            })
        }
        return {
            id: false,
            forename: app.authLevel <= 3,
            surname: app.authLevel <= 3,
            displayName: true,
            email: true,
            projectRole: typeFilter ? false : true,
        }
    }
    function loadUserColumnVisibility(): UserColumnVisibility {
        const localStorageValue = localStorage.getItem(app.applicationUser?.id + "_Available Users_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                forename: isMobile ? false : visibilityValues.find((v) => v.columnId === "forename")?.isVisible ?? true,
                surname: isMobile ? false : visibilityValues.find((v) => v.columnId === "surname")?.isVisible ?? true,
                displayName: visibilityValues.find((v) => v.columnId === "displayName")?.isVisible ?? true,
                email: visibilityValues.find((v) => v.columnId === "email")?.isVisible ?? true,
            })
        }
        return {
            id: false,
            forename: isMobile ? false : true,
            surname: true,
            displayName: true,
            email: true,
        }
    }

    const HandleAddUserToTeam = React.useCallback((demoUserIds?: string[]) => {
        if (demoMode) {
            // In demo mode, use functional updates to work with latest state
            setDemoUsers((prevUsers) => {
                setDemoTeamMembers((prevTeamMembers) => {
                    const selectedUsers = demoUserIds
                        ? prevUsers.filter((user) => demoUserIds.includes(String(user.id)))
                        : prevUsers.filter((user) => userSelection[String(user.id)]);
                    
                    return [...prevTeamMembers, ...selectedUsers.map((user) => ({ 
                        ...user, 
                        projectRole: typeFilter ? typeFilter : "Team Member" as const 
                    }))];
                });
                
                const selectedUsers = demoUserIds
                    ? prevUsers.filter((user) => demoUserIds.includes(String(user.id)))
                    : prevUsers.filter((user) => userSelection[String(user.id)]);
                
                return prevUsers.filter((user) => !selectedUsers.includes(user));
            });
            setUserSelection({});
            app.showActionNotification("Success", "Users added to team (Demo Mode)", "success");
            return;
        }

        const currentUsers = users;
        const selectedUsers = demoUserIds
            ? currentUsers.filter((user) => demoUserIds.includes(String(user.id)))
            : currentUsers.filter((user) => userSelection[String(user.id)]);

        const usersToAdd = selectedUsers.map((user) => ({
            userId: user.id,
            projectId: app.selectedProject?.id,
            roleId: getRoleIdFromRoleName(typeFilter ? typeFilter : "Team Member"),
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(usersToAdd),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers([...teamMembers, ...selectedUsers.map((user) => ({ ...user, projectRole: typeFilter ? typeFilter : "Team Member" as const }))]);
                    setUsers(users.filter((user) => !userSelection[String(user.id)]));
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error adding users to team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }, [demoMode, userSelection, typeFilter, users, teamMembers, app]);

    function HandleRemoveClicked(userId: string) {
        if (demoMode) {
            // In demo mode, just update local state
            const removedUser = demoTeamMembers.find((user) => user.id === userId);
            setDemoTeamMembers(demoTeamMembers.filter((user) => user.id !== userId));
            if (removedUser) {
                setDemoUsers([...demoUsers, removedUser]);
                setUserSelection({});
            }
            app.showActionNotification("Success", "User removed from team (Demo Mode)", "success");
            return;
        }

        const roles: ProjectUserRole[] = [
            {
                userId: userId,
                projectId: app.selectedProject?.id as number,
                projectName: "", // these values wont be required are we are deleting the user from the team
                roleName: "",
                roleId: 0,
            },
        ];

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.filter((user) => user.id !== userId));
                    const removedUser = teamMembers.find((user) => user.id === userId);
                    if (removedUser) {
                        setUsers([...users, removedUser]);
                        setUserSelection({});
                    }
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error removing user from team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    const HandleGroupRemoveClicked = React.useCallback(() => {
        if (demoMode) {
            // In demo mode, use functional updates to ensure we operate on latest state
            const userIdsToRemove = Object.keys(teamMemberSelection).filter(id => teamMemberSelection[id]);
            
            setDemoTeamMembers((prevTeamMembers) => {
                const removedUsers = prevTeamMembers.filter((user) => userIdsToRemove.includes(String(user.id)));
                // Update demoUsers with the removed team members
                setDemoUsers((prevUsers) => [...prevUsers, ...removedUsers]);
                // Return the filtered team members
                return prevTeamMembers.filter((user) => !userIdsToRemove.includes(String(user.id)));
            });
            
            setTeamMemberSelection({});
            setUserSelection({});
            app.showActionNotification("Success", "Users removed from team (Demo Mode)", "success");
            return;
        }

        const roles: ProjectUserRole[] = teamMembers.filter((user) => teamMemberSelection[String(user.id)]).map((user) => ({
            userId: user.id as string,
            projectId: app.selectedProject?.id as number,
            projectName: "", // these values wont be required are we are deleting the user from the team
            roleName: "",
            roleId: 0,
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.filter((user) => !teamMemberSelection[String(user.id)]));
                    setUsers([...users, ...teamMembers.filter((user) => teamMemberSelection[String(user.id)])]);
                    setTeamMemberSelection({});
                    setUserSelection({});
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error removing users from team: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }, [demoMode, teamMemberSelection, teamMembers, users, app]);

    function HandleSetRoleClicked(userid: string, role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") {
        if (demoMode) {
            // In demo mode, just update local state
            setDemoTeamMembers(demoTeamMembers.map((user) => {
                if (user.id === userid) {
                    return { ...user, projectRole: role };
                }
                return user;
            }));
            app.showActionNotification("Success", "Role updated (Demo Mode)", "success");
            return;
        }

        const roles: ProjectUserRole[] = [
            {
                userId: userid,
                projectId: app.selectedProject?.id as number,
                projectName: app.selectedProject?.name,
                roleName: role,
                roleId: getRoleIdFromRoleName(role),
            },
        ];

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.map((user) => {
                        if (user.id === userid) {
                            return { ...user, projectRole: role };
                        }
                        return user;
                    }));
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error setting role: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function HandleGroupSetRoleClicked(role: "Viewer" | "Team Member" | "Sponsor" | "Project Manager") {
        if (demoMode) {
            // In demo mode, just update local state
            setDemoTeamMembers(demoTeamMembers.map((user) => {
                if (teamMemberSelection[String(user.id)]) {
                    return { ...user, projectRole: role };
                }
                return user;
            }));
            setTeamMemberSelection({});
            setUserSelection({});
            app.showActionNotification("Success", "Roles updated (Demo Mode)", "success");
            return;
        }

        const roles: ProjectUserRole[] = teamMembers.filter((user) => teamMemberSelection[String(user.id)]).map((user) => ({
            userId: user.id as string,
            projectId: app.selectedProject?.id as number,
            projectName: app.selectedProject?.name,
            roleName: role,
            roleId: getRoleIdFromRoleName(role),
        }));

        document.body.style.cursor = 'wait';
        fetch("Team/", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(roles),
        })
            .then((result) => {
                if (result.ok) {
                    setTeamMembers(teamMembers.map((user) => {
                        if (teamMemberSelection[String(user.id)]) {
                            return { ...user, projectRole: role };
                        }
                        return user;
                    }));
                    setTeamMemberSelection({});
                    setUserSelection({});
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", "Error setting role: " + error, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    const teamMembersGroupActions = [
        {
            name: "Remove",
            action: HandleGroupRemoveClicked,
        }
    ];

    if (!typeFilter) {
        teamMembersGroupActions.push(
            {
                name: "Set Role - Viewer",
                action: () => HandleGroupSetRoleClicked("Viewer"),
            },
            {
                name: "Set Role - Team Member",
                action: () => HandleGroupSetRoleClicked("Team Member"),
            },
            {
                name: "Set Role - Sponsor",
                action: () => HandleGroupSetRoleClicked("Sponsor"),
            },
            {
                name: "Set Role - Project Manager",
                action: () => HandleGroupSetRoleClicked("Project Manager"),
            });
    }

    function populateDemoData() {
        const demoUsersList: User[] = [];
        const demoTeamList: TeamMember[] = [];
        
        // Generate 15 available demo users
        for (let i = 1; i <= 15; i++) {
            const firstName = `FirstName${i}`;
            const lastName = `LastName${i}`;
            demoUsersList.push({
                id: `demo-user-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `user${i}@demo.com`,
                userRole: "User",
            });
        }

        // Generate 5 team members with different roles
        const roles: ("Team Member" | "Viewer" | "Sponsor" | "Project Manager")[] = 
            ["Team Member", "Team Member", "Viewer", "Sponsor", "Project Manager"];
        
        for (let i = 1; i <= 5; i++) {
            const firstName = `TeamMember${i}`;
            const lastName = `Demo${i}`;
            demoTeamList.push({
                id: `demo-team-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `team${i}@demo.com`,
                userRole: "User",
                projectRole: roles[i - 1],
            });
        }

        setDemoUsers(demoUsersList);
        setDemoTeamMembers(demoTeamList);
    }

    // Set demo data to initial state (for wizard step navigation)
    const setTourStateInitial = React.useCallback(() => {
        const demoUsersList: User[] = [];
        const demoTeamList: TeamMember[] = [];
        
        // Generate 15 available demo users (all in users table)
        for (let i = 1; i <= 15; i++) {
            const firstName = `FirstName${i}`;
            const lastName = `LastName${i}`;
            demoUsersList.push({
                id: `demo-user-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `user${i}@demo.com`,
                userRole: "User",
            });
        }

        // Generate 5 team members
        const roles: ("Team Member" | "Viewer" | "Sponsor" | "Project Manager")[] = 
            ["Team Member", "Team Member", "Viewer", "Sponsor", "Project Manager"];
        
        for (let i = 1; i <= 5; i++) {
            const firstName = `TeamMember${i}`;
            const lastName = `Demo${i}`;
            demoTeamList.push({
                id: `demo-team-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `team${i}@demo.com`,
                userRole: "User",
                projectRole: roles[i - 1],
            });
        }

        setDemoUsers(demoUsersList);
        setDemoTeamMembers(demoTeamList);
        setUserSelection({});
        setTeamMemberSelection({});
    }, []);

    // Set demo data with demo-user-1 added to team (for wizard step 5)
    const setTourStateWithUserAdded = React.useCallback(() => {
        const demoUsersList: User[] = [];
        const demoTeamList: TeamMember[] = [];
        
        // Generate 14 available demo users (demo-user-1 is now in team)
        for (let i = 2; i <= 15; i++) {
            const firstName = `FirstName${i}`;
            const lastName = `LastName${i}`;
            demoUsersList.push({
                id: `demo-user-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `user${i}@demo.com`,
                userRole: "User",
            });
        }

        // Generate 5 original team members
        const roles: ("Team Member" | "Viewer" | "Sponsor" | "Project Manager")[] = 
            ["Team Member", "Team Member", "Viewer", "Sponsor", "Project Manager"];
        
        for (let i = 1; i <= 5; i++) {
            const firstName = `TeamMember${i}`;
            const lastName = `Demo${i}`;
            demoTeamList.push({
                id: `demo-team-${i}`,
                forename: firstName,
                surname: lastName,
                displayName: `${firstName} ${lastName}`,
                email: `team${i}@demo.com`,
                userRole: "User",
                projectRole: roles[i - 1],
            });
        }

        // Add demo-user-1 to the team
        demoTeamList.push({
            id: `demo-user-1`,
            forename: "FirstName1",
            surname: "LastName1",
            displayName: "FirstName1 LastName1",
            email: "user1@demo.com",
            userRole: "User",
            projectRole: "Team Member",
        });

        setDemoUsers(demoUsersList);
        setDemoTeamMembers(demoTeamList);
        setUserSelection({});
        setTeamMemberSelection({});
    }, []);

    function toggleDemoMode(enable: boolean) {
        if (enable) {
            // Entering demo mode - regenerate demo data
            populateDemoData();
            setDemoMode(true);
            setUserSelection({});
            setTeamMemberSelection({});
            app.showActionNotification("Demo Mode", "You are now in demo mode. All changes are local only.", "default");
        } else {
            // Exiting demo mode - no need to do anything special
            setDemoMode(false);
            setUserSelection({});
            setTeamMemberSelection({});
        }
    }

    const contextValue = {
        dialogOpen: false,
        setDialogOpen: () => {},
        demoMode,
        toggleDemoMode,
        showUserFilters,
        setShowUserFilters,
        showTeamFilters,
        setShowTeamFilters,
        setUserSelection,
        userSelection,
        setTeamMemberSelection,
        teamMemberSelection,
        HandleAddUserToTeam,
        HandleGroupRemoveClicked,
        setTourStateInitial,
        setTourStateWithUserAdded
    };

    // Use a ref to avoid stale closures in tour steps
    const contextRef = React.useRef(contextValue);
    React.useEffect(() => {
        contextRef.current = contextValue;
    }, [contextValue]);

    // Wait for demoMode to become true before starting tour
    React.useEffect(() => {
        if (shouldStartTour && demoMode && !tourStartedRef.current) {
            tourStartedRef.current = true;
            wizard.setWizardSteps(getBasicPageTourSteps({ teamBuilderContext: contextRef }));
            wizard.setGuidedTourCompleteFunctionCall(() => () => {
                tourStartedRef.current = false;
                toggleDemoMode(false);
                setShowUserFilters(false);
            });
            wizard.startGuidedTour();
            setShouldStartTour(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldStartTour, demoMode]);

    if (isMobile) {
        return (
            <div className={"flex flex-col w-full"}>
                <div className="flex flex-row justify-between mb-2">
                    <h1 className="flex flex-row text-4xl font-bold mx-auto">
                        Assigned
                        <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTeamFilters(!showTeamFilters)}>
                            <ShowFiltersIcon className='h-7 w-7' />
                        </div>
                    </h1>
                </div>
                <DataTable
                    columns={get_team_columns(HandleSetRoleClicked, HandleRemoveClicked, typeFilter, app.authLevel)}
                    data={demoMode ? demoTeamMembers : teamMembers}
                    setData={demoMode ? setDemoTeamMembers : setTeamMembers}
                    loading={loading}
                    viewOnly={app.authLevel < 3}
                    rowSelection={teamMemberSelection}
                    setRowSelection={setTeamMemberSelection}
                    groupActions={app.authLevel > 3 ? teamMembersGroupActions : undefined}
                    columnVisibility={loadTeamMemberColumnVisibility()}
                    table_title="Team Members"
                    columnDisplayNames={columnDisplayNames}
                    passedColumnFilters={typeFilter ? [{ id: "projectRole", value: typeFilter }] : undefined}
                    showFilters={showTeamFilters}
                />
                <button disabled={Object.keys(teamMemberSelection).length === 0} className="p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleGroupRemoveClicked}>Remove From Team</button>

                <Dialog>
                    <DialogTrigger>
                        {app.authLevel > 3 && (<button className="w-full mt-2 p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50">Add Team Member</button>)}
                    </DialogTrigger>
                    <DialogContent className="flex flex-col w-[95%]" style={{ zoom: zoomLevel }}>
                        <DialogHeader>
                            <div className="flex flex-row text-4xl font-bold">
                                <div>Available Users</div>
                                <div className="justify-end ml-2 self-end my-auto hover:cursor-pointer"
                                    onClick={() => setShowUserFilters(!showUserFilters)}>
                                    <ShowFiltersIcon className='h-7 w-7' />
                                </div>
                            </div>
                        </DialogHeader>
                        <DataTable
                            columns={user_columns}
                            data={demoMode ? demoUsers : users}
                            setData={demoMode ? setDemoUsers : setUsers}
                            loading={loading}
                            viewOnly={app.authLevel < 3}
                            rowSelection={userSelection}
                            setRowSelection={setUserSelection}
                            table_title="Available Users"
                            columnVisibility={loadUserColumnVisibility()}
                            columnDisplayNames={userColumnDisplayNames}
                            showFilters={showUserFilters}
                        />
                        <button disabled={Object.keys(userSelection).length === 0} className="p-2 mb-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={() => HandleAddUserToTeam()}>Add To Team</button>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    return (
        <TeamBuilderContext.Provider value={contextValue}>
        {showHelpLink && document.getElementById('title-container') && createPortal(
            <HelpLink page="team" />,
            document.getElementById('title-container')!
        )}
        <div className="flex flex-col">
            {wizard && wizard.getWizardPointer()}
            <div className="flex flex-row justify-end mb-2">
                {demoMode && <button className="rounded-md text-white bg-red-500 h-min px-3 py-1 my-auto ml-2" onClick={() => toggleDemoMode(false)}>Exit Demo Mode</button>}
            </div>
            <div className="flex flex-row justify-between">
                {app.authLevel > 3 && (
                <><div className="flex flex-col w-full">
                        <h1 className="flex flex-row text-lg font-bold mx-auto">
                            Available Users
                            <div id="show-user-filters" className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowUserFilters(!showUserFilters)}><ShowFiltersIcon className='h-4 w-4' /></div>
                        </h1>
                        <div id='users-table'>
                            <DataTable
                                columns={user_columns}
                                data={demoMode ? demoUsers : users}
                                setData={demoMode ? setDemoUsers : setUsers}
                                loading={loading}
                                viewOnly={app.authLevel < 3}
                                rowSelection={userSelection}
                                setRowSelection={setUserSelection}
                                table_title="Available Users"
                                columnVisibility={loadUserColumnVisibility()}
                                columnDisplayNames={userColumnDisplayNames}
                                showFilters={showUserFilters}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col w-[4%] items-center justify-center">
                        <button id='add-team-member-button' disabled={Object.keys(userSelection).length === 0} className="p-2 mb-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={() => HandleAddUserToTeam()}><ArrowRightIcon /></button>
                        <button id='remove-team-member-button' disabled={Object.keys(teamMemberSelection).length === 0} className="p-2 bg-gray-200 text-gray-600 rounded-sm disabled:opacity-50" onClick={HandleGroupRemoveClicked}><ArrowLeftIcon /></button>
                    </div>
                </>
            )}
            <div className={"flex flex-col " + (app.authLevel <= 3 ? " mx-auto" : " w-full")}>
                <h1 className="flex flex-row text-lg font-bold mx-auto">
                    Assigned
                    <div id="show-team-filters" className="justify-end ml-2 self-end my-auto hover:cursor-pointer" onClick={() => setShowTeamFilters(!showTeamFilters)}>
                        <ShowFiltersIcon className='h-4 w-4' />
                    </div>
                </h1>
                <div id='team-members-table'>
                    <DataTable
                        columns={get_team_columns(HandleSetRoleClicked, HandleRemoveClicked, typeFilter, app.authLevel)}
                        data={demoMode ? demoTeamMembers : teamMembers}
                        setData={demoMode ? setDemoTeamMembers : setTeamMembers}
                        loading={loading}
                        viewOnly={app.authLevel < 3}
                        rowSelection={teamMemberSelection}
                        setRowSelection={setTeamMemberSelection}
                        groupActions={app.authLevel > 3 ? teamMembersGroupActions : undefined}
                        columnVisibility={loadTeamMemberColumnVisibility()}
                        table_title="Team Members"
                        columnDisplayNames={columnDisplayNames}
                        passedColumnFilters={typeFilter ? [{ id: "projectRole", value: typeFilter }] : undefined}
                        showFilters={showTeamFilters}
                    />
                </div>
            </div>
        </div>
        <div className="flex flex-row w-min ml-auto">{wizard.getTourControls()}</div>
        {showHelpLink &&
            <TutorialChecker
                tutorialId={Tutorial.Team}
                tutorial={async () => {
                    tourStartedRef.current = false;
                    toggleDemoMode(true);
                    setShouldStartTour(true);
                }} />
        }
        </div>
        </TeamBuilderContext.Provider>
    )
}

export default TeamBuilder